// Execution strategy for choosing main-thread vs worker compile.
// Auto mode prefers worker only when source/import size suggests visible jank risk.
export type { LuisRuntimeCompileExecution } from './types'
import type { LuisRuntimeCompileExecution } from './types'
import type { BuildSourceEntry } from '../loader/process/types'
import type { LuisRuntimeWorkerMode } from '../loader/options'
import { getCompileHistory } from './history'

// Resolve compile execution path for one prepared source entry.
export function resolveCompileExecution(
  entry: BuildSourceEntry,
  options: { worker: LuisRuntimeWorkerMode },
): LuisRuntimeCompileExecution {
  // Honor explicit worker override from data-luis option tokens.
  if (options.worker === 'worker') {
    // Return worker execution for explicit worker override.
    return 'worker'
  } else if (options.worker === 'main') {
    // Keep explicit main-thread override.
    return 'main'
  } else if (entry.executionOverride === 'worker') {
    // Honor manifest worker override when data-luis stays in auto mode.
    return 'worker'
  } else if (entry.executionOverride === 'main') {
    // Honor manifest main-thread override when data-luis stays in auto mode.
    return 'main'
  } else {
    // Continue automatic execution selection.
  }

  const resolvedChars = estimateResolvedSourceChars(entry)
  const importedCount = entry.importedLuisHrefs.length
  const history = getCompileHistory(entry.kind === 'link' ? entry.href : entry.sourceId)
  const previousDurationMs = history?.durationMs ?? 0
  const lowEndDevice = isLowEndDevice()

  // Prefer worker for large resolved sources that likely block the UI thread.
  if (resolvedChars >= 80000) {
    // Return worker for oversized resolved source payload.
    return 'worker'
  } else if (importedCount >= 2 && resolvedChars >= 30000) {
    // Prefer worker when import graph produces a medium-large compile payload.
    return 'worker'
  } else if (previousDurationMs >= (lowEndDevice ? 8 : 12)) {
    // Reuse worker when recent compile time already crossed the jank threshold.
    return 'worker'
  } else {
    // Keep compile on main thread for cheap sources.
    return 'main'
  }
}

// Estimate effective source size including imported `.luis` payload lengths.
function estimateResolvedSourceChars(entry: BuildSourceEntry): number {
  const importedChars = entry.importMetrics.reduce((sum, metric) => sum + metric.sourceLength, 0)
  // Return raw source plus imported runtime `.luis` payload lengths.
  return entry.rawSource.length + importedChars
}

// Detect lower-end devices where main-thread jank thresholds should be lower.
function isLowEndDevice(): boolean {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined
  if (!nav) {
    // Return false when navigator runtime hints are unavailable.
    return false
  } else {
    // Continue low-end runtime hint checks.
  }

  const hardwareConcurrency = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : 8
  const deviceMemory = typeof (nav as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
    ? (nav as Navigator & { deviceMemory?: number }).deviceMemory ?? 8
    : 8
  // Return true for devices with low CPU or low memory hints.
  return hardwareConcurrency <= 4 || deviceMemory <= 4
}
